import { useState } from "react";
import { useParams } from "react-router-dom";
import { setToken, BASE } from "../lib/api";
import { useResource, titleCase } from "../lib/hooks";
import { AuthShell, Icon } from "../components/AuthShell";

interface InviteInfo {
    fullName: string;
    email: string;
    role: string;
    kind: "invite" | "reset";
}

/**
 * The invite landing page — also the password-reset landing page. Same
 * token/expiry/single-use link, same "set your own password" form; `kind`
 * only changes the copy and what happens on submit (create an account vs.
 * update an existing one), both server-side. Nobody else ever knows the
 * password chosen here, which is the whole point of both flows.
 */
export const InviteAccept = () => {
    const { token } = useParams<{ token: string }>();
    const invite = useResource<InviteInfo>(token ? `/invites/token/${token}` : null);

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isReset = invite.data?.kind === "reset";

    const accept = async () => {
        if (password !== confirm) {
            setError("The passwords do not match.");
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const response = await fetch(`${BASE}/invites/token/${token}/accept`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password }),
            });
            const body = await response.json().catch(() => null);
            if (!response.ok) {
                throw new Error(body?.error ?? (isReset ? "Could not reset the password" : "Could not accept the invite"));
            }
            setToken(body.token);
            window.location.href = "/";
        } catch (err) {
            setError(err instanceof Error ? err.message : (isReset ? "Could not reset the password" : "Could not accept the invite"));
            setBusy(false);
        }
    };

    const eyeIcon = (shown: boolean) =>
        shown
            ? <Icon><path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-2.16 2.92M6.06 6.06A13.3 13.3 0 0 0 2 11s3.5 7 10 7a9.1 9.1 0 0 0 4.94-1.06M9.9 9.9a3 3 0 1 0 4.2 4.2" /><path d="M2 2l20 20" /></Icon>
            : <Icon><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></Icon>;

    return (
        <AuthShell
            eyebrow={isReset ? "Account recovery" : "Set up access"}
            title={isReset ? "Reset password" : "Welcome"}
        >
            <div className="tcl-form">
                {invite.error && <div className="tcl-error">{invite.error}</div>}

                {invite.data && (
                    <>
                        {isReset ? (
                            <p className="tcl-lead-text">
                                Choose a new password for <strong>{invite.data.fullName}</strong><br />
                                <span className="mono">{invite.data.email}</span>
                            </p>
                        ) : (
                            <p className="tcl-lead-text">
                                Welcome, <strong>{invite.data.fullName}</strong>.<br />
                                Your account: <span className="mono">{invite.data.email}</span>{" "}
                                <span className="chip info">{titleCase(invite.data.role)}</span>
                            </p>
                        )}

                        {error && <div className="tcl-error">{error}</div>}

                        <div className="tcl-field tcl-field--pw">
                            <label htmlFor="ia-password">New password</label>
                            <div className="tcl-control">
                                <span className="tcl-lead">
                                    <Icon><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></Icon>
                                </span>
                                <input id="ia-password" type={showPw ? "text" : "password"} value={password}
                                       onChange={(e) => setPassword(e.target.value)}
                                       placeholder="Choose a password (12+ characters)" autoComplete="new-password" />
                                <button type="button" className="tcl-eye" onClick={() => setShowPw((s) => !s)}
                                        aria-pressed={showPw} aria-label={showPw ? "Hide password" : "Show password"}>
                                    {eyeIcon(showPw)}
                                </button>
                            </div>
                        </div>

                        <div className="tcl-field tcl-field--pw">
                            <label htmlFor="ia-confirm">Confirm password</label>
                            <div className="tcl-control">
                                <span className="tcl-lead">
                                    <Icon><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></Icon>
                                </span>
                                <input id="ia-confirm" type={showConfirm ? "text" : "password"} value={confirm}
                                       onChange={(e) => setConfirm(e.target.value)}
                                       placeholder="Confirm password" autoComplete="new-password" />
                                <button type="button" className="tcl-eye" onClick={() => setShowConfirm((s) => !s)}
                                        aria-pressed={showConfirm} aria-label={showConfirm ? "Hide password" : "Show password"}>
                                    {eyeIcon(showConfirm)}
                                </button>
                            </div>
                        </div>

                        <button className="tcl-btn" disabled={busy || password.length === 0}
                                onClick={() => void accept()}>
                            <Icon><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="M10 17l5-5-5-5" /><path d="M15 12H3" /></Icon>
                            {busy ? "Saving…" : isReset ? "Set new password & sign in" : "Set password & sign in"}
                        </button>

                        <p className="tcl-hint">Only you know this password. This link works once and then dies.</p>
                    </>
                )}
            </div>
        </AuthShell>
    );
};
